import AttractMode from './control_modes/attract'
import ModeSwitcher from 'js/render/modeSwitcher'
import MouseMode from './control_modes/mouse'
import RenderEventDispatcher from './eventDispatcher'
import {Vector3} from 'three'

// Camera control interface to the viewer.
class Controls extends RenderEventDispatcher {
  // Constructor
  constructor(viewer) {
    super(viewer)

    // Standard quanta of rotation. Roughly about one degree.
    // Used by control modes to increment the orbit position.
    this.orbitIncrement = Math.PI / 180

    this.$ = this.$.bind(this)
    this.selectMode = this.selectMode.bind(this)
    this.initControlModes = this.initControlModes.bind(this)
    this.initModeMenu = this.initModeMenu.bind(this)
    this.orbitDistance = this.orbitDistance.bind(this)
    this.orbitRight = this.orbitRight.bind(this)
    this.orbitUp = this.orbitUp.bind(this)
    this.translateUp = this.translateUp.bind(this)
    this.translateLeft = this.translateLeft.bind(this)
    this.changeZoom = this.changeZoom.bind(this)
    this.orbitLeft = this.orbitLeft.bind(this)
    this.orbitDown = this.orbitDown.bind(this)
    this.translateDown = this.translateDown.bind(this)
    this.translateRight = this.translateRight.bind(this)
    this.update = this.update.bind(this)
    this.updateCenter = this.updateCenter.bind(this)
    this.viewer = viewer
    this.viewer.addEventListener('added', this.updateCenter)
    this.viewer.addEventListener('tick', this.update)

    this.renderer = this.viewer.renderer
    this.camera = this.viewer.camera
    this.center = new Vector3(0, 0, 0)

    const distance = this.orbitDistance()
    this.camera.position.set(0, -(distance / 2), distance).setLength(distance)

    this.initModeMenu()
    this.initControlModes()
    this.updateCenter()
  }

  // Utils
  $() {
    if (!this.$container) {
      this.$container = $(this.renderer.domElement)
    }
    return this.$container
  }

  selectMode(mode) {
    window.debug(`Switching to ${mode} mode`)
    for (const name of Object.keys(this.viewer.objects)) {
      const obj = this.viewer.objects[name]
      obj.switchMode(mode)
    }
  }

  initControlModes() {
    this.modes = {
      attract: new AttractMode(this, true),
      mouse: new MouseMode(this)
    }

    this.modes.mouse.onUse = () => {
      for (const name of Object.keys(this.modes)) {
        const mode = this.modes[name]
        mode.deactivate()
      }
      this.modes.mouse.activate()
    }
    this.modes.mouse.onRelease = () => {
      for (const name of Object.keys(this.modes)) {
        const mode = this.modes[name]
        mode.deactivate()
      }

      this.modes.attract.activate()
    }
  }

  initModeMenu() {
    return new ModeSwitcher(this.selectMode)
  }

  orbitDistance() {
    const zoom = 5
    const radius = object => object.geometry.boundingSphere.radius || 0
    const viewerObjects = this.viewer.objects
    const voKeys = Object.keys(viewerObjects)
    const max = Math.max(voKeys.map(name => radius(viewerObjects[name])))
    if (max > 0) {
      return max * zoom
    } else {
      return 100
    }
  }

  // Yeild to fn after offsetting `object` by `-offeset` first
  // then calling `fn` then adding back the offset vector as
  // `+offset` to `object.position` to make orbital rotations
  // and length scaling of camera position easier.
  withoutOriginOffset(object, offset, fn) {
    object.position.sub(offset)
    fn()
    object.position.add(offset)
  }

  // Direct camera controls
  orbitRight(angle) {
    this.withoutOriginOffset(this.camera, this.center, () => {
      const axis = new Vector3(0, 0, 1)
      this.camera.position.applyAxisAngle(axis, angle)
      this.camera.up.applyAxisAngle(axis, angle)
    })
  }

  orbitUp(angle) {
    this.withoutOriginOffset(this.camera, this.center, () => {
      const axis = this.camera.position.clone().cross(this.camera.up).normalize()
      this.camera.position.applyAxisAngle(axis, angle)
      this.camera.up.applyAxisAngle(axis, angle)
    })
  }

  translateUp(change) {
    const translation = this.camera.up.clone().normalize().multiplyScalar(change)
    this.camera.position.add(translation)
    this.center.add(translation)
  }

  translateLeft(change) {
    const up = this.camera.up.clone().normalize()
    const position = this.camera.position.clone().normalize()
    const translation = up.cross(position).multiplyScalar(change)
    this.camera.position.add(translation)
    this.center.add(translation)
  }

  changeZoom(delta) {
    this.withoutOriginOffset(this.camera, this.center, () => {
      if (delta > 0) {
        this.camera.position.multiplyScalar(1.02)
      } else if (delta < 0) {
        this.camera.position.multiplyScalar(0.98)
      }
    })
  }

  // Sign inverting camera controls
  orbitLeft(angle) {
    return this.orbitRight(-angle)
  }
  orbitDown(angle) {
    return this.orbitUp(-angle)
  }
  translateDown(change) {
    return this.translateUp(-change)
  }
  translateRight(change) {
    return this.translateLeft(-change)
  }

  // Handlers
  update() {
    this.camera.lookAt(this.center)
    this.dispatchEvent({type: 'tick', data: 0}) // TODO: Fill in dt
  }

  updateCenter() {
    let bounds = undefined
    this.viewer.scene.traverse(o => {
      let geom
      if (o.mesh) {
        return
      }
      if (!o.geometry) {
        return
      }
      ;(geom = o.geometry).computeBoundingBox()
      if (!bounds) {
        bounds = geom.boundingBox.clone()
      } else {
        bounds.expandByPoint(geom.boundingBox.min)
        bounds.expandByPoint(geom.boundingBox.max)
      }
    })
    if (bounds) {
      this.center = bounds.getCenter(new Vector3()).clone()
    }
    window.debug('Center updated, now:', this.center)
  }
}

export default Controls
